import React, { useState, useEffect, useCallback } from "react";
import { Container, Button, ToastContainer, Spinner, Row, Col, Form, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FaEdit, FaTrashAlt, FaSearch, FaEraser, FaBan } from "react-icons/fa";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { toast } from "react-toastify";
import "../../Styles/AssetRegister.css";
import ScaleLoader from "react-spinners/ScaleLoader";
import { formatNumber } from "../../utils/formatNumber";

const AssetRegister = () => {
    const navigate = useNavigate();
    const customer_currency = (localStorage.getItem("customer_currency") || "").replace(/"/g, "");
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filter State
    const [filters, setFilters] = useState({
        asset_type: "",
        asset_name: "",
        asset_description: "",
        asset_date: "",
        asset_amount: ""
    });

    // Disposal / write-off modal state
    const [showDisposeModal, setShowDisposeModal] = useState(false);
    const [disposeAsset, setDisposeAsset] = useState(null);
    const [disposeForm, setDisposeForm] = useState({
        disposal_date: "",
        disposal_status: "disposed",
        disposal_proceeds: "",
        disposal_reason: "",
    });
    const [isDisposing, setIsDisposing] = useState(false);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    // Main Data Fetching
    const fetchInitialData = useCallback(async () => {
        setLoading(true);
        try {
            const payload = {
                customer_id: Number(localStorage.getItem("customer_id")) || 1005,
                api_key: localStorage.getItem("api_key") || "b9359f494e77b6589ce12dd62518c585",
                language: "en",
                user_id: localStorage.getItem("user_id") || 14857,
                limit: 10,
                page: 1,
                asset_type: filters.asset_type,
                asset_name: filters.asset_name,
                asset_description: filters.asset_description,
                asset_date: filters.asset_date,
                asset_amount: filters.asset_amount ? Number(filters.asset_amount) : ""
            };

            const response = await simplePostCall(ApiConfig.getassets, JSON.stringify(payload));

            if (response && response.result) {
                setAssets(response.data || []);
            } else {
                setAssets([]);
            }
        } catch (error) {
            console.error("Error fetching assets:", error);
            toast.error("Failed to load assets");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    // Delete Logic
    const handleDelete = async (assetId) => {
        if (window.confirm("Are you sure you want to delete this asset?")) {
            try {
                const payload = {
                    customer_id: Number(localStorage.getItem("customer_id")) || 1005,
                    api_key: localStorage.getItem("api_key") || "b9359f494e77b6589ce12dd62518c585",
                    user_id: localStorage.getItem("user_id") || 14857,
                    asset_id: assetId
                };

                // Using delete endpoint from your ApiConfig
                const response = await simplePostCall(ApiConfig.Deleteeassets, JSON.stringify(payload));

                if (response && response.result) {
                    toast.success("Asset deleted successfully");
                    fetchInitialData(); // Refresh list
                } else {
                    toast.error(response.message || "Delete failed");
                }
            } catch (error) {
                console.error("Error deleting asset:", error);
                toast.error("An error occurred during deletion");
            }
        }
    };

    const resetFilters = () => {
        setFilters({
            asset_type: "",
            asset_name: "",
            asset_description: "",
            asset_date: "",
            asset_amount: ""
        });
    };

    // Open the disposal/write-off modal pre-filled with asset context
    const openDisposeModal = (asset) => {
        setDisposeAsset(asset);
        setDisposeForm({
            disposal_date: new Date().toISOString().slice(0, 10),
            disposal_status: "disposed",
            disposal_proceeds: "",
            disposal_reason: "",
        });
        setShowDisposeModal(true);
    };

    const handleDisposeChange = (e) => {
        const { name, value } = e.target;
        setDisposeForm((prev) => ({ ...prev, [name]: value }));
    };

    // Submit a disposal/write-off request. Backend marks the asset's status,
    // logs the financial impact (cost vs proceeds = gain/loss), and the asset
    // remains in the register with its updated status — it isn't hard-deleted.
    const submitDisposal = async () => {
        if (!disposeAsset) return;
        if (!disposeForm.disposal_date) {
            toast.warning("Please select a disposal date.");
            return;
        }
        setIsDisposing(true);
        try {
            const payload = {
                customer_id: Number(localStorage.getItem("customer_id")) || 1005,
                api_key: localStorage.getItem("api_key") || "b9359f494e77b6589ce12dd62518c585",
                user_id: localStorage.getItem("user_id") || 14857,
                language: "en",
                asset_id: disposeAsset.asset_id,
                asset_status: disposeForm.disposal_status,
                disposal_date: disposeForm.disposal_date,
                disposal_proceeds: disposeForm.disposal_proceeds
                    ? Number(disposeForm.disposal_proceeds)
                    : 0,
                disposal_reason: disposeForm.disposal_reason,
            };
            const res = await simplePostCall(
                ApiConfig.disposeasset || ApiConfig.Updateassets,
                JSON.stringify(payload),
            );
            if (res?.result) {
                toast.success(
                    `Asset ${disposeForm.disposal_status === "disposed" ? "disposed" : "written off"} successfully.`,
                );
                setShowDisposeModal(false);
                fetchInitialData();
            } else {
                toast.error(res?.message || "Disposal failed");
            }
        } catch (err) {
            console.error("Dispose error:", err);
            toast.error("An error occurred during disposal.");
        } finally {
            setIsDisposing(false);
        }
    };

    const statusBadgeClass = (status) => {
        const s = String(status || "active").toLowerCase();
        if (s === "disposed") return "asset-status-pill asset-status-disposed";
        if (s === "damaged") return "asset-status-pill asset-status-damaged";
        return "asset-status-pill asset-status-active";
    };

    return (
     <div>
  <ToastContainer position="top-right" />

  <Container fluid>
    {/* Topbar */}
    <div className="d-flex justify-content-between align-items-center mb-3 bg-white rounded mt-4 p-2">
      <h3>Assets</h3>

      <Link to="/accountant/finance/asset/create" style={{ textDecoration: "none" }}>
        <Button style={{ background: "#53b7e8", border: "none" }}>
          + Add Asset
        </Button>
      </Link>
    </div>

    {/* Filter Section */}
    <div className="p-2 mb-2 mt-2 rounded"
    style={{margin: '0px 0px 0px -23px'}}>
      <Row className="g-2">
        <Col md={2}>
          <Form.Control
            name="asset_type"
            placeholder="Type"
            value={filters.asset_type}
            onChange={handleFilterChange}
          />
        </Col>

        <Col md={2}>
          <Form.Control
            name="asset_name"
            placeholder="Asset Name"
            value={filters.asset_name}
            onChange={handleFilterChange}
          />
        </Col>

        <Col md={2}>
          <Form.Control
            name="asset_description"
            placeholder="Description"
            value={filters.asset_description}
            onChange={handleFilterChange}
          />
        </Col>

        <Col md={2}>
          <Form.Control
            type="date"
            name="asset_date"
            value={filters.asset_date}
            onChange={handleFilterChange}
          />
        </Col>

        <Col md={2}>
          <Form.Control
            type="number"
            name="asset_amount"
            placeholder="Amount"
            value={filters.asset_amount}
            onChange={handleFilterChange}
          />
        </Col>

        <Col md={2} className="d-flex gap-2">
          <Button
            style={{ background: "#53b7e8", border: "none" }}
            onClick={fetchInitialData}
          >
            <FaSearch />
          </Button>

          <Button variant="secondary" onClick={resetFilters}>
            <FaEraser />
          </Button>
        </Col>
      </Row>
    </div>

    {/* Asset Table */}
    <div className="asset-register-custom">

      {/* Header */}
      <div className="asset-header-row-2 px-2 py-3 mb-2 fw-bold text-muted d-none d-lg-flex">
        <div className="col-asset-1">Sr No</div>
        <div className="col-asset-1">Category</div>
        <div className="col-asset-1">Name</div>
        <div className="col-asset-1">Location</div>
        <div className="col-asset-1">Purchase Date</div>
        <div className="col-asset-1">Cost</div>
        <div className="col-asset-1">Useful Life</div>
        <div className="col-asset-1">Status</div>
        <div className="col-asset-1 text-center">Actions</div>
      </div>

      {/* Rows */}
      {loading ? (
        <div className="text-center p-2">
          <ScaleLoader color="#F4BB44" />
        </div>
      ) : assets.length > 0 ? (
        assets.map((item, index) => (
          <div
            key={item.asset_id}
            className="asset-row-2 p-2 mb-2 bg-white border rounded d-flex"
          >
            <div className="col-asset-1">{index + 1}</div>
            <div className="col-asset-1">{item.asset_type}</div>
            <div className="col-asset-1 fw-medium" title={item.asset_description}>
              {item.asset_name}
            </div>

            <div className="col-asset-1 text-truncate">
              {item.asset_location || "—"}
            </div>

            <div className="col-asset-1">
              {item.asset_date ? new Date(item.asset_date).toLocaleDateString() : "—"}
            </div>

            <div className="col-asset-1">
              {customer_currency} {formatNumber(item.asset_amount)}
            </div>

            <div className="col-asset-1">
              {item.asset_useful_life_years
                ? `${item.asset_useful_life_years} yrs`
                : "—"}
            </div>

            <div className="col-asset-1">
              <span className={statusBadgeClass(item.asset_status)}>
                {(item.asset_status || "active").toUpperCase()}
              </span>
            </div>

            <div className="col-asset-1 d-flex gap-2 justify-content-center">
              <button
                className="btn btn-sm"
                title="Edit"
                onClick={() =>
                  navigate(`/accountant/finance/asset/create/${item.asset_id}`)
                }
              >
                <FaEdit />
              </button>

              {String(item.asset_status || "active").toLowerCase() !== "disposed" && (
                <button
                  className="btn btn-sm"
                  title="Dispose / Write-Off"
                  onClick={() => openDisposeModal(item)}
                >
                  <FaBan color="#b45309" />
                </button>
              )}

              <button
                className="btn btn-sm"
                title="Delete"
                onClick={() => handleDelete(item.asset_id)}
              >
                <FaTrashAlt color="red" />
              </button>
            </div>
          </div>
        ))
      ) : (
        <div className="text-center p-2 bg-white border rounded">
          No assets match your search.
        </div>
      )}
    </div>

    {/* Disposal / Write-Off Modal */}
    <Modal
      show={showDisposeModal}
      onHide={() => !isDisposing && setShowDisposeModal(false)}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Dispose / Write-Off Asset</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {disposeAsset && (
          <div className="mb-3 p-2 rounded" style={{ background: "#f8f9fa" }}>
            <div><b>{disposeAsset.asset_name}</b></div>
            <div className="text-muted" style={{ fontSize: 13 }}>
              {disposeAsset.asset_type} • Purchase cost {customer_currency}{" "}
              {formatNumber(disposeAsset.asset_amount)}
            </div>
          </div>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Disposition <span className="text-danger">*</span></Form.Label>
          <Form.Select
            name="disposal_status"
            value={disposeForm.disposal_status}
            onChange={handleDisposeChange}
          >
            <option value="disposed">Disposed (sold / transferred)</option>
            <option value="damaged">Damaged (write-off)</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Disposal Date <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="date"
            name="disposal_date"
            value={disposeForm.disposal_date}
            onChange={handleDisposeChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Proceeds Received ({customer_currency})</Form.Label>
          <Form.Control
            type="number"
            step="0.01"
            min="0"
            name="disposal_proceeds"
            placeholder="0 if written off"
            value={disposeForm.disposal_proceeds}
            onChange={handleDisposeChange}
          />
          <Form.Text className="text-muted">
            Enter sale proceeds if disposed. Leave 0 for damaged write-offs.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-1">
          <Form.Label>Reason / Notes</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            name="disposal_reason"
            placeholder="e.g. End of useful life, sold to staff, water damage"
            value={disposeForm.disposal_reason}
            onChange={handleDisposeChange}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => setShowDisposeModal(false)}
          disabled={isDisposing}
        >
          Cancel
        </Button>
        <Button variant="warning" onClick={submitDisposal} disabled={isDisposing}>
          {isDisposing ? <Spinner size="sm" /> : "Confirm Disposal"}
        </Button>
      </Modal.Footer>
    </Modal>
  </Container>
</div>

    );
};

export default AssetRegister;